import { Moon, Pipette, RefreshCw, Sun } from 'lucide-react';

import {
  APPROVED_ACCENTS,
  Button,
  Panel,
  StateSurface,
  StatusPill,
  useTheme,
  type AccentId,
} from '../../design-system/index.js';
import {
  createIntentKey,
  useCreationCapabilitiesQuery,
  useCreationPresetsQuery,
  useRequestSynchronizationMutation,
  useSettingsDiscovery,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { PageHeader, SectionLabel, formatTime } from '../shared/page.js';

export function SettingsPage() {
  const workspace = useWorkspace();
  const discovery = useSettingsDiscovery(workspace.connection.id);
  const capabilities = useCreationCapabilitiesQuery(workspace.connection.id);
  const presets = useCreationPresetsQuery(workspace.connection.id);
  const sync = useRequestSynchronizationMutation();
  const theme = useTheme();

  const loading =
    discovery.connections.isLoading ||
    discovery.libraries.isLoading ||
    discovery.destinations.isLoading ||
    discovery.synchronizations.isLoading ||
    capabilities.isLoading ||
    presets.isLoading;
  const error =
    discovery.connections.error ??
    discovery.libraries.error ??
    discovery.destinations.error ??
    discovery.synchronizations.error ??
    capabilities.error ??
    presets.error;
  if (loading) return <StateSurface kind="loading" title="Loading workspace settings" />;
  if (error)
    return (
      <StateSurface
        kind="error"
        title="Settings unavailable"
        action={
          <Button
            variant="secondary"
            onClick={() =>
              void Promise.all([
                discovery.connections.refetch(),
                discovery.libraries.refetch(),
                discovery.destinations.refetch(),
                discovery.synchronizations.refetch(),
                capabilities.refetch(),
                presets.refetch(),
              ])
            }
          >
            Try again
          </Button>
        }
      />
    );

  const connections = discovery.connections.data?.items ?? [];
  const libraries = discovery.libraries.data?.items ?? [];
  const destinations = discovery.destinations.data?.items ?? [];
  const tasks = discovery.synchronizations.data?.items ?? [];

  return (
    <div>
      <PageHeader title="Settings" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel>
          <SectionLabel>Connection</SectionLabel>
          <div className="mt-4 grid gap-3">
            {connections.map((connection) => (
              <div key={connection.id} className="flex items-center gap-3">
                <StatusPill status={connection.status} />
                <span className="text-sm font-medium">{connection.name}</span>
                <span className="ml-auto font-mono text-xs text-ink-3">
                  {connection.id.slice(0, 8)}
                </span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel>
          <SectionLabel>Managed Library</SectionLabel>
          <div className="mt-4 grid gap-3">
            {libraries.map((library) => (
              <div key={library.id} className="flex items-center gap-3">
                <StatusPill status={library.status} />
                <span className="text-sm font-medium">{library.name}</span>
                <span className="ml-auto font-mono text-xs text-ink-3">owned WAV</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel>
          <SectionLabel>Destinations</SectionLabel>
          <div className="mt-4">
            {destinations.length ? (
              <div className="grid gap-3">
                {destinations.map((destination) => (
                  <div key={destination.id} className="flex items-center gap-3">
                    <StatusPill status={destination.status} />
                    <span className="text-sm font-medium">{destination.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-3">
                No active destinations. Delivery actions remain hidden.
              </p>
            )}
          </div>
        </Panel>
        <Panel>
          <SectionLabel>Creation</SectionLabel>
          {capabilities.data ? (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {capabilities.data.modes.map((mode) => (
                  <span
                    key={mode}
                    className="rounded-full border border-accent-line bg-accent-soft px-2.5 py-1 font-mono text-xs text-accent"
                  >
                    {mode}
                  </span>
                ))}
                {capabilities.data.controls.lyrics.supported &&
                capabilities.data.controls.lyrics.modes.includes('none') ? (
                  <span className="rounded-full border border-border bg-bg-2 px-2.5 py-1 font-mono text-xs text-ink-2">
                    song / instrumental
                  </span>
                ) : null}
                {capabilities.data.controls.lyrics.supported ? (
                  <span className="rounded-full border border-border bg-bg-2 px-2.5 py-1 font-mono text-xs text-ink-2">
                    {capabilities.data.controls.lyrics.modes.join(' / ')} lyrics
                  </span>
                ) : null}
              </div>
              <p className="mt-4 text-sm text-ink-2">
                {presets.data?.items.length ?? 0} creation{' '}
                {(presets.data?.items.length ?? 0) === 1 ? 'preset' : 'presets'}
              </p>
            </div>
          ) : null}
        </Panel>
        <Panel className="lg:col-span-2">
          <div className="flex items-center justify-between gap-4">
            <SectionLabel>Synchronization</SectionLabel>
            {discovery.synchronizations.isFetching ? (
              <RefreshCw className="size-4 animate-spin text-ink-3" />
            ) : null}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {(['catalog', 'favorites'] as const).map((kind) => {
              const task = tasks
                .filter((item) => item.synchronization_kind === kind)
                .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
              const canRetry = task?.actions.includes('retry');
              return (
                <div key={kind} className="rounded-md border border-border bg-bg-2 p-4">
                  <div className="flex items-center gap-3">
                    <span className="font-medium capitalize">{kind}</span>
                    {task ? <StatusPill status={task.status} /> : <StatusPill status="idle" />}
                    <Button
                      size="small"
                      variant="secondary"
                      className="ml-auto"
                      disabled={
                        sync.isPending || (task && ['queued', 'running'].includes(task.status))
                      }
                      onClick={() =>
                        void sync
                          .mutateAsync({
                            connectionId: workspace.connection.id,
                            body: { synchronization_kind: kind },
                            idempotencyKey: createIntentKey(),
                          })
                          .then(() => discovery.synchronizations.refetch())
                      }
                    >
                      {canRetry ? 'Retry' : 'Sync now'}
                    </Button>
                  </div>
                  {task?.failure ? (
                    <p className="mt-3 text-xs text-err">{task.failure.message}</p>
                  ) : null}
                  {task ? (
                    <p className="mt-3 font-mono text-xs text-ink-3">
                      Updated {formatTime(task.updated_at)} · attempt {task.attempt_count}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
          {sync.error ? (
            <p className="mt-4 text-sm text-err">Synchronization could not be requested.</p>
          ) : null}
        </Panel>
        <Panel className="lg:col-span-2">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <SectionLabel>Appearance</SectionLabel>
            <Button variant="secondary" onClick={theme.toggleMode}>
              {theme.mode === 'dark' ? <Sun /> : <Moon />}
              {theme.mode === 'dark' ? 'Use light theme' : 'Use dark theme'}
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap gap-3" role="radiogroup" aria-label="Accent color">
            {(
              Object.entries(APPROVED_ACCENTS) as [AccentId, (typeof APPROVED_ACCENTS)[AccentId]][]
            ).map(([id, accent]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={theme.accent === id}
                onClick={() => theme.setAccent(id)}
                className={`flex min-h-11 items-center gap-3 rounded-sm border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent-ring ${theme.accent === id ? 'border-accent bg-accent-soft text-ink' : 'border-border bg-bg text-ink-2'}`}
              >
                <span
                  className="size-5 rounded-full border border-white/20"
                  style={{ background: accent.value }}
                />
                {accent.label}
              </button>
            ))}
            <button
              type="button"
              role="radio"
              aria-checked={theme.accent === 'custom'}
              onClick={() => theme.setCustomAccent(theme.customAccent)}
              className={`flex min-h-11 items-center gap-3 rounded-sm border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent-ring ${theme.accent === 'custom' ? 'border-accent bg-accent-soft text-ink' : 'border-border bg-bg text-ink-2'}`}
            >
              <span
                aria-hidden="true"
                className="size-5 rounded-full border border-white/20"
                style={{ background: theme.customAccent }}
              />
              Custom
            </button>
            <label className="relative grid size-11 cursor-pointer place-items-center rounded-sm border border-border bg-bg text-ink-2 outline-none focus-within:ring-2 focus-within:ring-accent-ring">
              <input
                aria-label="Edit custom accent color"
                className="absolute inset-0 cursor-pointer opacity-0"
                type="color"
                value={theme.customAccent}
                onChange={(event) => theme.setCustomAccent(event.currentTarget.value)}
              />
              <Pipette aria-hidden="true" className="size-4" />
            </label>
          </div>
        </Panel>
      </div>
    </div>
  );
}
